/**
 * CrossEntityWorkflowPage - Visual workflow mapping and cross-entity simulation
 *
 * EQ-PROV-007 Patent Claims:
 *   Claim F: Visual workflow mapping with two-dimensional monotonicity enforcement
 *   Claim G: Cross-entity workflow simulation with dry-run capsule verification
 *   Claim B: Scope monotonicity (visualized in sidebar)
 *   Claim K: Three-way minimum tier computation (visualized in waterfall)
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, Badge, Button, Select, TextInput, Alert, Modal, Textarea, Spinner, Table } from 'flowbite-react';
import { Make_Authorized_API_Call, isSuccessResponse } from '@equilateral/ui-shared';
import { HiPlus, HiPencil, HiTrash, HiExclamation } from 'react-icons/hi';
import { useStatus } from '../../contexts/StatusProvider';
import { API_ENDPOINTS } from '../../services/api/endpoints';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface Touchpoint {
  touchpoint_id: string;
  touchpoint_name: string;
  origin_entity_id: string;
  origin_entity_name: string;
  target_entity_id: string;
  target_entity_name: string;
  action_category: string;
  typical_consequence_tier: number;
  status: string;
  created_at: string;
  updated_at: string;
}

interface CrossEntityPolicy {
  policy_id: string;
  policy_name: string;
  origin_entity_id: string;
  origin_entity_name: string;
  target_entity_id: string;
  target_entity_name: string;
  tier_ceiling: number;
  permitted_categories: string[];
  status: string;
  created_at: string;
  updated_at: string;
}

interface PolicyFormData {
  policy_name: string;
  origin_entity_id: string;
  target_entity_id: string;
  tier_ceiling: number;
  permitted_categories: string;
  status: string;
}

interface AuditEvent {
  audit_id: string;
  event_type: string;
  origin_entity_name: string;
  target_entity_name: string;
  action_category: string;
  consequence_tier: number;
  result: string;
  details: string;
  created_at: string;
}

const EMPTY_POLICY_FORM: PolicyFormData = {
  policy_name: '',
  origin_entity_id: '',
  target_entity_id: '',
  tier_ceiling: 3,
  permitted_categories: '',
  status: 'active',
};

type SimulationResult = 'PASS' | 'HOLD' | 'DENY';

interface SimulationStep {
  label: string;
  detail: string;
  status: 'info' | 'success' | 'warning' | 'error';
}

interface SimulationState {
  result: SimulationResult | null;
  effective_tier: number;
  steps: SimulationStep[];
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const TIER_COLORS: Record<number, string> = {
  1: 'bg-green-500',
  2: 'bg-lime-500',
  3: 'bg-yellow-500',
  4: 'bg-orange-500',
  5: 'bg-red-500',
};

const TIER_BADGE_COLORS: Record<number, 'success' | 'warning' | 'failure' | 'info' | 'purple'> = {
  1: 'success',
  2: 'success',
  3: 'warning',
  4: 'failure',
  5: 'failure',
};

const SCOPE_LEVELS = [
  { name: 'Platform', color: 'bg-gray-400', textColor: 'text-gray-800' },
  { name: 'Client', color: 'bg-yellow-400', textColor: 'text-yellow-900' },
  { name: 'Cross-Entity', color: 'bg-blue-400', textColor: 'text-blue-900' },
  { name: 'Entity', color: 'bg-green-400', textColor: 'text-green-900' },
];

const MOCK_MATURITY_CEILING = 3;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function uniqueEntities(touchpoints: Touchpoint[]): string[] {
  const set = new Set<string>();
  for (const tp of touchpoints) {
    set.add(tp.origin_entity_name);
    set.add(tp.target_entity_name);
  }
  return Array.from(set).sort();
}

function entityIdsFromTouchpoints(touchpoints: Touchpoint[]): { id: string; name: string }[] {
  const map = new Map<string, string>();
  for (const tp of touchpoints) {
    map.set(tp.origin_entity_id, tp.origin_entity_name);
    map.set(tp.target_entity_id, tp.target_entity_name);
  }
  return Array.from(map.entries()).map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
}

function actionCategoriesFromTouchpoints(touchpoints: Touchpoint[]): string[] {
  const set = new Set<string>();
  for (const tp of touchpoints) {
    if (tp.action_category) set.add(tp.action_category);
  }
  return Array.from(set).sort();
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

/** Swim-lane diagram (Claim F) */
const SwimLaneDiagram: React.FC<{ touchpoints: Touchpoint[] }> = ({ touchpoints }) => {
  const entities = uniqueEntities(touchpoints);

  if (entities.length === 0) {
    return (
      <div className="flex items-center justify-center h-full text-gray-400">
        No touchpoints configured. Register touchpoints to see the workflow map.
      </div>
    );
  }

  const laneHeight = 80;
  const svgHeight = entities.length * laneHeight + 40;
  const nodeWidth = 160;
  const nodeHeight = 48;

  // Distribute touchpoints horizontally
  const tpPerPair = new Map<string, Touchpoint[]>();
  for (const tp of touchpoints) {
    const key = `${tp.origin_entity_name}|${tp.target_entity_name}`;
    if (!tpPerPair.has(key)) tpPerPair.set(key, []);
    tpPerPair.get(key)!.push(tp);
  }

  const laneY = (entityName: string) => {
    const idx = entities.indexOf(entityName);
    return 30 + idx * laneHeight + laneHeight / 2;
  };

  let xOffset = 0;
  const nodes: { tp: Touchpoint; x: number; originY: number; targetY: number }[] = [];
  for (const [, tps] of tpPerPair) {
    for (const tp of tps) {
      nodes.push({
        tp,
        x: 180 + xOffset * (nodeWidth + 40),
        originY: laneY(tp.origin_entity_name),
        targetY: laneY(tp.target_entity_name),
      });
      xOffset++;
    }
  }

  const svgWidth = Math.max(600, 180 + nodes.length * (nodeWidth + 40) + 40);

  return (
    <div className="overflow-x-auto">
      <svg width={svgWidth} height={svgHeight} className="min-w-full">
        {/* Swim lane backgrounds */}
        {entities.map((entity, idx) => (
          <g key={entity}>
            <rect
              x={0}
              y={30 + idx * laneHeight}
              width={svgWidth}
              height={laneHeight}
              fill={idx % 2 === 0 ? '#f9fafb' : '#f3f4f6'}
              stroke="#e5e7eb"
              strokeWidth={1}
            />
            <text
              x={12}
              y={30 + idx * laneHeight + laneHeight / 2 + 5}
              className="text-xs"
              fill="#374151"
              fontWeight="600"
              fontSize={13}
            >
              {entity}
            </text>
          </g>
        ))}

        {/* Connection lines and nodes */}
        {nodes.map(({ tp, x, originY, targetY }, i) => {
          const midY = (originY + targetY) / 2;
          const tierColor =
            tp.typical_consequence_tier <= 2
              ? '#22c55e'
              : tp.typical_consequence_tier <= 3
                ? '#eab308'
                : '#ef4444';

          return (
            <g key={tp.touchpoint_id || i}>
              {/* Line from origin lane to node */}
              <line
                x1={x + nodeWidth / 2}
                y1={originY}
                x2={x + nodeWidth / 2}
                y2={midY - nodeHeight / 2}
                stroke={tierColor}
                strokeWidth={2}
                strokeDasharray="4 2"
              />
              {/* Line from node to target lane */}
              <line
                x1={x + nodeWidth / 2}
                y1={midY + nodeHeight / 2}
                x2={x + nodeWidth / 2}
                y2={targetY}
                stroke={tierColor}
                strokeWidth={2}
                strokeDasharray="4 2"
              />
              {/* Arrow head on target end */}
              <polygon
                points={`${x + nodeWidth / 2 - 5},${targetY - 8} ${x + nodeWidth / 2 + 5},${targetY - 8} ${x + nodeWidth / 2},${targetY}`}
                fill={tierColor}
              />
              {/* Node rectangle */}
              <rect
                x={x}
                y={midY - nodeHeight / 2}
                width={nodeWidth}
                height={nodeHeight}
                rx={8}
                fill="white"
                stroke={tierColor}
                strokeWidth={2}
              />
              {/* Node text */}
              <text
                x={x + nodeWidth / 2}
                y={midY - 4}
                textAnchor="middle"
                fontSize={11}
                fontWeight="600"
                fill="#1f2937"
              >
                {tp.touchpoint_name.length > 18
                  ? tp.touchpoint_name.substring(0, 16) + '...'
                  : tp.touchpoint_name}
              </text>
              {/* Tier badge */}
              <rect
                x={x + nodeWidth / 2 - 16}
                y={midY + 6}
                width={32}
                height={16}
                rx={8}
                fill={tierColor}
              />
              <text
                x={x + nodeWidth / 2}
                y={midY + 18}
                textAnchor="middle"
                fontSize={10}
                fontWeight="bold"
                fill="white"
              >
                T{tp.typical_consequence_tier}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
};

/** Tier Waterfall Chart (Claim K: three-way minimum) */
const TierWaterfallChart: React.FC<{
  touchpoints: Touchpoint[];
  policies: CrossEntityPolicy[];
}> = ({ touchpoints, policies }) => {
  if (touchpoints.length === 0) {
    return (
      <div className="flex items-center justify-center h-full text-gray-400 text-sm">
        No touchpoints to visualize.
      </div>
    );
  }

  const displayTouchpoints = touchpoints.slice(0, 8); // Limit for readability
  const barGroupWidth = 100;
  const barWidth = 20;
  const chartHeight = 200;
  const maxTier = 5;
  const svgWidth = displayTouchpoints.length * barGroupWidth + 60;

  const tierToHeight = (tier: number) => (tier / maxTier) * (chartHeight - 30);

  return (
    <div className="overflow-x-auto">
      <svg width={svgWidth} height={chartHeight + 40}>
        {/* Y-axis labels */}
        {[1, 2, 3, 4, 5].map((t) => (
          <g key={t}>
            <text
              x={20}
              y={chartHeight - tierToHeight(t) + 4}
              fontSize={10}
              fill="#6b7280"
              textAnchor="middle"
            >
              T{t}
            </text>
            <line
              x1={35}
              y1={chartHeight - tierToHeight(t)}
              x2={svgWidth}
              y2={chartHeight - tierToHeight(t)}
              stroke="#e5e7eb"
              strokeWidth={1}
            />
          </g>
        ))}

        {displayTouchpoints.map((tp, idx) => {
          const groupX = 50 + idx * barGroupWidth;

          // Find policy for this touchpoint pair
          const policy = policies.find(
            (p) =>
              p.origin_entity_id === tp.origin_entity_id &&
              p.target_entity_id === tp.target_entity_id
          );
          const policyCeiling = policy ? policy.tier_ceiling : 5;
          const capsuleTier = tp.typical_consequence_tier;
          const effectiveTier = Math.min(capsuleTier, policyCeiling, MOCK_MATURITY_CEILING);

          return (
            <g key={tp.touchpoint_id || idx}>
              {/* Capsule Tier bar (blue) */}
              <rect
                x={groupX}
                y={chartHeight - tierToHeight(capsuleTier)}
                width={barWidth}
                height={tierToHeight(capsuleTier)}
                fill="#3b82f6"
                rx={2}
              />
              {/* Policy Ceiling bar (orange) */}
              <rect
                x={groupX + barWidth + 4}
                y={chartHeight - tierToHeight(policyCeiling)}
                width={barWidth}
                height={tierToHeight(policyCeiling)}
                fill="#f97316"
                rx={2}
              />
              {/* Maturity Ceiling bar (purple) */}
              <rect
                x={groupX + (barWidth + 4) * 2}
                y={chartHeight - tierToHeight(MOCK_MATURITY_CEILING)}
                width={barWidth}
                height={tierToHeight(MOCK_MATURITY_CEILING)}
                fill="#a855f7"
                rx={2}
              />
              {/* Effective tier line (red dashed) */}
              <line
                x1={groupX - 4}
                y1={chartHeight - tierToHeight(effectiveTier)}
                x2={groupX + (barWidth + 4) * 3}
                y2={chartHeight - tierToHeight(effectiveTier)}
                stroke="#ef4444"
                strokeWidth={2}
                strokeDasharray="6 3"
              />
              {/* Touchpoint label */}
              <text
                x={groupX + barGroupWidth / 2 - 10}
                y={chartHeight + 16}
                fontSize={9}
                fill="#374151"
                textAnchor="middle"
              >
                {tp.touchpoint_name.length > 12
                  ? tp.touchpoint_name.substring(0, 10) + '..'
                  : tp.touchpoint_name}
              </text>
            </g>
          );
        })}

        {/* Legend */}
        <g transform={`translate(${svgWidth - 200}, 10)`}>
          <rect x={0} y={0} width={10} height={10} fill="#3b82f6" rx={2} />
          <text x={14} y={9} fontSize={9} fill="#374151">Capsule Tier</text>
          <rect x={0} y={16} width={10} height={10} fill="#f97316" rx={2} />
          <text x={14} y={25} fontSize={9} fill="#374151">Policy Ceiling</text>
          <rect x={0} y={32} width={10} height={10} fill="#a855f7" rx={2} />
          <text x={14} y={41} fontSize={9} fill="#374151">Maturity Ceiling</text>
          <line x1={0} y1={52} x2={10} y2={52} stroke="#ef4444" strokeWidth={2} strokeDasharray="4 2" />
          <text x={14} y={55} fontSize={9} fill="#ef4444">Effective Tier (min)</text>
        </g>
      </svg>
    </div>
  );
};

/** Scope Monotonicity Indicator (Claim B) */
const ScopeMonotonicityIndicator: React.FC = () => (
  <Card className="h-full">
    <h3 className="text-sm font-semibold text-gray-700 mb-3">Scope Monotonicity</h3>
    <p className="text-xs text-gray-500 mb-4">
      Scope can only decrease through the dispatch chain (Claim B).
    </p>
    <div className="flex flex-col items-center space-y-1">
      {SCOPE_LEVELS.map((level, idx) => (
        <div key={level.name} className="flex flex-col items-center w-full">
          <div
            className={`${level.color} ${level.textColor} rounded-lg px-3 py-2 text-center text-xs font-semibold w-full`}
          >
            {level.name}
          </div>
          {idx < SCOPE_LEVELS.length - 1 && (
            <svg width={20} height={20} className="my-0.5">
              <polygon points="10,16 4,6 16,6" fill="#6b7280" />
            </svg>
          )}
        </div>
      ))}
    </div>
    <div className="mt-3 flex items-center justify-center text-xs text-gray-500">
      <svg width={14} height={14} className="mr-1">
        <polygon points="7,12 2,4 12,4" fill="#6b7280" />
      </svg>
      Can only decrease
    </div>
  </Card>
);

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

export const CrossEntityWorkflowPage: React.FC = () => {
  const { Client_ID } = useStatus();

  // Data state
  const [touchpoints, setTouchpoints] = useState<Touchpoint[]>([]);
  const [policies, setPolicies] = useState<CrossEntityPolicy[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  // Simulator state
  const [originEntityId, setOriginEntityId] = useState('');
  const [targetEntityId, setTargetEntityId] = useState('');
  const [actionCategory, setActionCategory] = useState('');
  const [consequenceTier, setConsequenceTier] = useState(1);
  const [simulation, setSimulation] = useState<SimulationState | null>(null);
  const [isSimulating, setIsSimulating] = useState(false);

  // Policy CRUD state
  const [policyModalOpen, setPolicyModalOpen] = useState(false);
  const [editingPolicy, setEditingPolicy] = useState<CrossEntityPolicy | null>(null);
  const [policyForm, setPolicyForm] = useState<PolicyFormData>({ ...EMPTY_POLICY_FORM });
  const [policySubmitting, setPolicySubmitting] = useState(false);
  const [policySubmitError, setPolicySubmitError] = useState('');
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deletingPolicy, setDeletingPolicy] = useState<CrossEntityPolicy | null>(null);
  const [policyDeleting, setPolicyDeleting] = useState(false);

  // Audit log state
  const [auditEvents, setAuditEvents] = useState<AuditEvent[]>([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [showAuditLog, setShowAuditLog] = useState(false);

  // Derived data
  const entities = useMemo(() => entityIdsFromTouchpoints(touchpoints), [touchpoints]);
  const categories = useMemo(() => actionCategoriesFromTouchpoints(touchpoints), [touchpoints]);

  // ---------------------------------------------------------------------------
  // Data fetching
  // ---------------------------------------------------------------------------

  const fetchData = useCallback(async () => {
    if (!Client_ID) return;

    setIsLoading(true);
    setError('');

    try {
      const [touchpointsRes, policiesRes] = await Promise.allSettled([
        Make_Authorized_API_Call<Touchpoint>(
          `${API_ENDPOINTS.GOVERNANCE.CROSS_ENTITY_TOUCHPOINTS_GET}?Client_ID=${Client_ID}`,
          'GET'
        ),
        Make_Authorized_API_Call<CrossEntityPolicy>(
          `${API_ENDPOINTS.GOVERNANCE.CROSS_ENTITY_POLICIES_GET}?Client_ID=${Client_ID}`,
          'GET'
        ),
      ]);

      if (touchpointsRes.status === 'fulfilled' && isSuccessResponse(touchpointsRes.value)) {
        setTouchpoints(touchpointsRes.value.data.Records || []);
      }
      if (policiesRes.status === 'fulfilled' && isSuccessResponse(policiesRes.value)) {
        setPolicies(policiesRes.value.data.Records || []);
      }
    } catch (err) {
      console.error('Error fetching workflow data:', err);
      setError('Failed to load workflow data. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [Client_ID]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // ---------------------------------------------------------------------------
  // Simulation (Claim G)
  // ---------------------------------------------------------------------------

  const runSimulation = useCallback(() => {
    setIsSimulating(true);
    const steps: SimulationStep[] = [];

    // Step 1: Capsule Created
    steps.push({
      label: 'Capsule Created',
      detail: `Tier ${consequenceTier} | Scope: Cross-Entity | Category: ${actionCategory || 'N/A'}`,
      status: 'info',
    });

    // Step 2: Policy Evaluation
    const policy = policies.find(
      (p) => p.origin_entity_id === originEntityId && p.target_entity_id === targetEntityId
    );

    if (!policy) {
      steps.push({
        label: 'Policy Evaluated',
        detail: 'No policy found between selected entities.',
        status: 'error',
      });
      steps.push({
        label: 'Three-Way Min Applied',
        detail: 'Cannot compute - no governing policy.',
        status: 'error',
      });
      steps.push({
        label: 'Gate Decision',
        detail: 'DENY - No cross-entity policy exists for this entity pair.',
        status: 'error',
      });
      setSimulation({ result: 'DENY', effective_tier: 0, steps });
      setIsSimulating(false);
      return;
    }

    steps.push({
      label: 'Policy Evaluated',
      detail: `Policy: ${policy.policy_name} | Tier Ceiling: ${policy.tier_ceiling} | Categories: ${policy.permitted_categories.join(', ')}`,
      status: 'info',
    });

    // Step 3: Three-Way Minimum (Claim K)
    const effectiveTier = Math.min(consequenceTier, policy.tier_ceiling, MOCK_MATURITY_CEILING);
    steps.push({
      label: 'Three-Way Min Applied',
      detail: `min(Capsule=${consequenceTier}, Policy=${policy.tier_ceiling}, Maturity=${MOCK_MATURITY_CEILING}) = ${effectiveTier}`,
      status: 'info',
    });

    // Step 4: Gate Decision
    const categoryPermitted =
      !actionCategory || policy.permitted_categories.includes(actionCategory);

    let result: SimulationResult;
    if (!categoryPermitted) {
      result = 'DENY';
      steps.push({
        label: 'Gate Decision',
        detail: `DENY - Category "${actionCategory}" is not in permitted categories.`,
        status: 'error',
      });
    } else if (effectiveTier >= 4) {
      result = 'HOLD';
      steps.push({
        label: 'Gate Decision',
        detail: `HOLD - Effective tier ${effectiveTier} >= 4 requires human review.`,
        status: 'warning',
      });
    } else {
      result = 'PASS';
      steps.push({
        label: 'Gate Decision',
        detail: `PASS - Effective tier ${effectiveTier}, category permitted. Dispatch authorized.`,
        status: 'success',
      });
    }

    setSimulation({ result, effective_tier: effectiveTier, steps });
    setIsSimulating(false);
  }, [originEntityId, targetEntityId, actionCategory, consequenceTier, policies]);

  // ---------------------------------------------------------------------------
  // Policy CRUD handlers
  // ---------------------------------------------------------------------------

  const openPolicyCreateModal = () => {
    setEditingPolicy(null);
    setPolicyForm({ ...EMPTY_POLICY_FORM });
    setPolicySubmitError('');
    setPolicyModalOpen(true);
  };

  const openPolicyEditModal = (policy: CrossEntityPolicy) => {
    setEditingPolicy(policy);
    setPolicyForm({
      policy_name: policy.policy_name,
      origin_entity_id: policy.origin_entity_id,
      target_entity_id: policy.target_entity_id,
      tier_ceiling: policy.tier_ceiling,
      permitted_categories: policy.permitted_categories.join(', '),
      status: policy.status,
    });
    setPolicySubmitError('');
    setPolicyModalOpen(true);
  };

  const closePolicyModal = () => {
    setPolicyModalOpen(false);
    setEditingPolicy(null);
    setPolicySubmitError('');
  };

  const handlePolicySubmit = async () => {
    if (!policyForm.policy_name.trim()) {
      setPolicySubmitError('Policy name is required.');
      return;
    }
    setPolicySubmitting(true);
    setPolicySubmitError('');
    try {
      const isEdit = editingPolicy !== null;
      const url = isEdit
        ? API_ENDPOINTS.GOVERNANCE.CROSS_ENTITY_POLICY_UPDATE
        : API_ENDPOINTS.GOVERNANCE.CROSS_ENTITY_POLICY_CREATE;
      const method = isEdit ? 'PUT' : 'POST';

      const body: Record<string, unknown> = {
        policy_name: policyForm.policy_name,
        origin_entity_id: policyForm.origin_entity_id,
        target_entity_id: policyForm.target_entity_id,
        tier_ceiling: Number(policyForm.tier_ceiling),
        permitted_categories: policyForm.permitted_categories
          .split(',')
          .map((c) => c.trim())
          .filter(Boolean),
        status: policyForm.status,
        Client_ID: Client_ID,
      };

      if (isEdit) {
        body.policy_id = editingPolicy.policy_id;
      }

      const response = await Make_Authorized_API_Call(url, method, body);
      if (!response?.success) {
        throw new Error(
          (response as any)?.error_details || `Failed to ${isEdit ? 'update' : 'create'} policy`
        );
      }

      closePolicyModal();
      await fetchData();
    } catch (err) {
      setPolicySubmitError(err instanceof Error ? err.message : 'Operation failed');
    } finally {
      setPolicySubmitting(false);
    }
  };

  const updatePolicyField = (field: keyof PolicyFormData, value: string | number) => {
    setPolicyForm((prev) => ({ ...prev, [field]: value }));
  };

  const openDeletePolicyModal = (policy: CrossEntityPolicy) => {
    setDeletingPolicy(policy);
    setDeleteModalOpen(true);
  };

  const closeDeletePolicyModal = () => {
    setDeleteModalOpen(false);
    setDeletingPolicy(null);
  };

  const handleDeletePolicy = async () => {
    if (!deletingPolicy) return;
    setPolicyDeleting(true);
    try {
      const url = API_ENDPOINTS.GOVERNANCE.CROSS_ENTITY_POLICY_UPDATE;
      const response = await Make_Authorized_API_Call(url, 'DELETE', {
        policy_id: deletingPolicy.policy_id,
      });
      if (!response?.success) {
        throw new Error((response as any)?.error_details || 'Failed to delete policy');
      }
      closeDeletePolicyModal();
      await fetchData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete policy');
      closeDeletePolicyModal();
    } finally {
      setPolicyDeleting(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Audit log
  // ---------------------------------------------------------------------------

  const fetchAuditLog = useCallback(async () => {
    if (!Client_ID) return;
    setAuditLoading(true);
    try {
      const url = `${API_ENDPOINTS.GOVERNANCE.CROSS_ENTITY_AUDIT_GET}?Client_ID=${Client_ID}&limit=20`;
      const response = await Make_Authorized_API_Call(url, 'GET');
      if (response?.success) {
        setAuditEvents(response.data?.Records || []);
      }
    } catch (err) {
      console.error('Failed to fetch audit log:', err);
    } finally {
      setAuditLoading(false);
    }
  }, [Client_ID]);

  const toggleAuditLog = () => {
    const next = !showAuditLog;
    setShowAuditLog(next);
    if (next && auditEvents.length === 0) {
      fetchAuditLog();
    }
  };

  // ---------------------------------------------------------------------------
  // Render helpers
  // ---------------------------------------------------------------------------

  const resultBadgeColor = (result: SimulationResult): 'success' | 'warning' | 'failure' => {
    switch (result) {
      case 'PASS': return 'success';
      case 'HOLD': return 'warning';
      case 'DENY': return 'failure';
    }
  };

  const stepStatusClasses = (status: SimulationStep['status']): string => {
    switch (status) {
      case 'success': return 'border-green-400 bg-green-50';
      case 'warning': return 'border-yellow-400 bg-yellow-50';
      case 'error': return 'border-red-400 bg-red-50';
      default: return 'border-blue-400 bg-blue-50';
    }
  };

  const stepDotClasses = (status: SimulationStep['status']): string => {
    switch (status) {
      case 'success': return 'bg-green-500';
      case 'warning': return 'bg-yellow-500';
      case 'error': return 'bg-red-500';
      default: return 'bg-blue-500';
    }
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
        <span className="ml-3 text-gray-600">Loading workflow data...</span>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full min-h-screen bg-gray-50">
      {/* Header */}
      <div className="px-6 py-4 bg-white border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Cross-Entity Workflow Builder & Simulator
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              EQ-PROV-007 Claims F & G: Visual workflow mapping with simulation
            </p>
          </div>
          <Button size="sm" color="light" onClick={fetchData}>
            Refresh
          </Button>
        </div>
      </div>

      {error && (
        <div className="px-6 pt-4">
          <Alert color="failure" onDismiss={() => setError('')}>
            {error}
          </Alert>
        </div>
      )}

      {/* Main layout: content + scope sidebar */}
      <div className="flex flex-1 overflow-hidden">
        {/* Main content area */}
        <div className="flex-1 flex flex-col overflow-auto p-6 space-y-6">
          {/* Top: Swim-lane diagram (Claim F) */}
          <Card className="flex-shrink-0" style={{ minHeight: '40vh' }}>
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg font-semibold text-gray-800">
                Workflow Map — Swim-Lane Diagram
              </h2>
              <div className="flex items-center space-x-2 text-xs text-gray-500">
                <Badge color="success" size="xs">T1-T2</Badge>
                <Badge color="warning" size="xs">T3</Badge>
                <Badge color="failure" size="xs">T4-T5</Badge>
                <span className="ml-1">{touchpoints.length} touchpoint(s)</span>
              </div>
            </div>
            <SwimLaneDiagram touchpoints={touchpoints} />
          </Card>

          {/* Bottom row: Waterfall + Simulator */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Bottom-left: Tier Waterfall (Claim K) */}
            <Card>
              <h2 className="text-lg font-semibold text-gray-800 mb-2">
                Tier Waterfall — Three-Way Minimum
              </h2>
              <p className="text-xs text-gray-500 mb-3">
                Effective tier = min(Capsule, Policy Ceiling, Maturity Ceiling) at each touchpoint.
              </p>
              <TierWaterfallChart touchpoints={touchpoints} policies={policies} />
            </Card>

            {/* Bottom-right: Simulator (Claim G) */}
            <Card>
              <h2 className="text-lg font-semibold text-gray-800 mb-2">
                Simulate Dispatch
              </h2>
              <p className="text-xs text-gray-500 mb-4">
                Dry-run a cross-entity dispatch to verify policy and tier gating.
              </p>

              <div className="grid grid-cols-2 gap-3 mb-4">
                {/* Origin Entity */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Origin Entity
                  </label>
                  <Select
                    sizing="sm"
                    value={originEntityId}
                    onChange={(e) => setOriginEntityId(e.target.value)}
                  >
                    <option value="">Select origin...</option>
                    {entities.map((ent) => (
                      <option key={ent.id} value={ent.id}>
                        {ent.name}
                      </option>
                    ))}
                  </Select>
                </div>

                {/* Target Entity */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Target Entity
                  </label>
                  <Select
                    sizing="sm"
                    value={targetEntityId}
                    onChange={(e) => setTargetEntityId(e.target.value)}
                  >
                    <option value="">Select target...</option>
                    {entities.map((ent) => (
                      <option key={ent.id} value={ent.id}>
                        {ent.name}
                      </option>
                    ))}
                  </Select>
                </div>

                {/* Action Category */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Action Category
                  </label>
                  <Select
                    sizing="sm"
                    value={actionCategory}
                    onChange={(e) => setActionCategory(e.target.value)}
                  >
                    <option value="">Select category...</option>
                    {categories.map((cat) => (
                      <option key={cat} value={cat}>
                        {cat}
                      </option>
                    ))}
                  </Select>
                </div>

                {/* Consequence Tier */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Consequence Tier
                  </label>
                  <Select
                    sizing="sm"
                    value={consequenceTier}
                    onChange={(e) => setConsequenceTier(Number(e.target.value))}
                  >
                    {[1, 2, 3, 4, 5].map((t) => (
                      <option key={t} value={t}>
                        Tier {t}
                      </option>
                    ))}
                  </Select>
                </div>
              </div>

              <Button
                size="sm"
                color="blue"
                className="w-full mb-4"
                onClick={runSimulation}
                disabled={!originEntityId || !targetEntityId || isSimulating}
              >
                {isSimulating ? 'Simulating...' : 'Run Simulation'}
              </Button>

              {/* Simulation Results */}
              {simulation && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-semibold text-gray-700">Result:</span>
                    <Badge color={resultBadgeColor(simulation.result!)} size="sm">
                      {simulation.result}
                    </Badge>
                  </div>

                  {/* Step-by-step flow */}
                  <div className="relative">
                    {simulation.steps.map((step, idx) => (
                      <div key={idx} className="flex items-start mb-3 last:mb-0">
                        {/* Timeline connector */}
                        <div className="flex flex-col items-center mr-3">
                          <div
                            className={`w-3 h-3 rounded-full ${stepDotClasses(step.status)}`}
                          />
                          {idx < simulation.steps.length - 1 && (
                            <div className="w-0.5 h-8 bg-gray-300 mt-1" />
                          )}
                        </div>
                        {/* Step content */}
                        <div
                          className={`flex-1 border-l-2 pl-3 py-1 rounded-r ${stepStatusClasses(step.status)}`}
                        >
                          <div className="text-xs font-semibold text-gray-800">
                            {step.label}
                          </div>
                          <div className="text-xs text-gray-600 mt-0.5">
                            {step.detail}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </Card>
          </div>

          {/* Policy Management Section */}
          <Card>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-800">
                Cross-Entity Dispatch Policies
              </h2>
              <Button size="sm" color="blue" onClick={openPolicyCreateModal}>
                <HiPlus className="mr-1 h-4 w-4" />
                New Policy
              </Button>
            </div>

            {policies.length === 0 ? (
              <p className="text-sm text-gray-500">
                No policies configured. Create a policy to govern cross-entity dispatches.
              </p>
            ) : (
              <div className="overflow-x-auto">
                <Table striped>
                  <Table.Head>
                    <Table.HeadCell>Policy Name</Table.HeadCell>
                    <Table.HeadCell>Origin</Table.HeadCell>
                    <Table.HeadCell>Target</Table.HeadCell>
                    <Table.HeadCell>Tier Ceiling</Table.HeadCell>
                    <Table.HeadCell>Categories</Table.HeadCell>
                    <Table.HeadCell>Status</Table.HeadCell>
                    <Table.HeadCell>Actions</Table.HeadCell>
                  </Table.Head>
                  <Table.Body className="divide-y">
                    {policies.map((policy) => (
                      <Table.Row key={policy.policy_id}>
                        <Table.Cell className="font-medium text-gray-900">
                          {policy.policy_name}
                        </Table.Cell>
                        <Table.Cell>{policy.origin_entity_name}</Table.Cell>
                        <Table.Cell>{policy.target_entity_name}</Table.Cell>
                        <Table.Cell>
                          <Badge color={TIER_BADGE_COLORS[policy.tier_ceiling] || 'gray'}>
                            T{policy.tier_ceiling}
                          </Badge>
                        </Table.Cell>
                        <Table.Cell>
                          <div className="flex flex-wrap gap-1">
                            {policy.permitted_categories.slice(0, 3).map((cat) => (
                              <Badge key={cat} color="info" size="xs">
                                {cat}
                              </Badge>
                            ))}
                            {policy.permitted_categories.length > 3 && (
                              <Badge color="gray" size="xs">
                                +{policy.permitted_categories.length - 3}
                              </Badge>
                            )}
                          </div>
                        </Table.Cell>
                        <Table.Cell>
                          <Badge
                            color={policy.status === 'active' ? 'success' : 'gray'}
                          >
                            {policy.status}
                          </Badge>
                        </Table.Cell>
                        <Table.Cell>
                          <div className="flex gap-1">
                            <Button
                              size="xs"
                              color="light"
                              onClick={() => openPolicyEditModal(policy)}
                            >
                              <HiPencil className="h-3.5 w-3.5" />
                            </Button>
                            <Button
                              size="xs"
                              color="failure"
                              onClick={() => openDeletePolicyModal(policy)}
                            >
                              <HiTrash className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        </Table.Cell>
                      </Table.Row>
                    ))}
                  </Table.Body>
                </Table>
              </div>
            )}
          </Card>

          {/* Audit Log Section */}
          <Card>
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-lg font-semibold text-gray-800">
                Cross-Entity Audit Log
              </h2>
              <div className="flex gap-2">
                <Button size="sm" color="light" onClick={toggleAuditLog}>
                  {showAuditLog ? 'Hide' : 'Show'} Recent Events
                </Button>
                {showAuditLog && (
                  <Button size="sm" color="light" onClick={fetchAuditLog}>
                    Refresh
                  </Button>
                )}
              </div>
            </div>

            {showAuditLog && (
              <>
                {auditLoading ? (
                  <div className="flex justify-center py-6">
                    <Spinner size="md" />
                    <span className="ml-2 text-sm text-gray-500">Loading audit events...</span>
                  </div>
                ) : auditEvents.length === 0 ? (
                  <p className="text-sm text-gray-500 py-4">No audit events found.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <Table striped>
                      <Table.Head>
                        <Table.HeadCell>Time</Table.HeadCell>
                        <Table.HeadCell>Event</Table.HeadCell>
                        <Table.HeadCell>Origin</Table.HeadCell>
                        <Table.HeadCell>Target</Table.HeadCell>
                        <Table.HeadCell>Tier</Table.HeadCell>
                        <Table.HeadCell>Result</Table.HeadCell>
                      </Table.Head>
                      <Table.Body className="divide-y">
                        {auditEvents.map((evt) => (
                          <Table.Row key={evt.audit_id}>
                            <Table.Cell className="text-xs whitespace-nowrap">
                              {new Date(evt.created_at).toLocaleString()}
                            </Table.Cell>
                            <Table.Cell className="text-xs">{evt.event_type}</Table.Cell>
                            <Table.Cell className="text-xs">{evt.origin_entity_name}</Table.Cell>
                            <Table.Cell className="text-xs">{evt.target_entity_name}</Table.Cell>
                            <Table.Cell>
                              <Badge
                                color={
                                  evt.consequence_tier <= 2
                                    ? 'success'
                                    : evt.consequence_tier <= 3
                                      ? 'warning'
                                      : 'failure'
                                }
                                size="xs"
                              >
                                T{evt.consequence_tier}
                              </Badge>
                            </Table.Cell>
                            <Table.Cell>
                              <Badge
                                color={
                                  evt.result === 'PASS'
                                    ? 'success'
                                    : evt.result === 'HOLD'
                                      ? 'warning'
                                      : 'failure'
                                }
                                size="xs"
                              >
                                {evt.result}
                              </Badge>
                            </Table.Cell>
                          </Table.Row>
                        ))}
                      </Table.Body>
                    </Table>
                  </div>
                )}
              </>
            )}
          </Card>
        </div>

        {/* Far right: Scope Monotonicity sidebar (Claim B) */}
        <div className="w-48 flex-shrink-0 p-4 border-l border-gray-200 bg-white">
          <ScopeMonotonicityIndicator />
        </div>
      </div>

      {/* Policy Create/Edit Modal */}
      <Modal show={policyModalOpen} onClose={closePolicyModal} size="lg">
        <Modal.Header>
          {editingPolicy ? 'Edit Policy' : 'Create Dispatch Policy'}
        </Modal.Header>
        <Modal.Body>
          <div className="space-y-4">
            {policySubmitError && (
              <div className="p-3 bg-red-50 text-red-700 rounded-lg text-sm">
                {policySubmitError}
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Policy Name
              </label>
              <TextInput
                value={policyForm.policy_name}
                onChange={(e) => updatePolicyField('policy_name', e.target.value)}
                placeholder="e.g. Finance-to-HR Data Sync"
                required
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Origin Entity
                </label>
                <Select
                  value={policyForm.origin_entity_id}
                  onChange={(e) => updatePolicyField('origin_entity_id', e.target.value)}
                >
                  <option value="">Select origin...</option>
                  {entities.map((ent) => (
                    <option key={ent.id} value={ent.id}>
                      {ent.name}
                    </option>
                  ))}
                </Select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Target Entity
                </label>
                <Select
                  value={policyForm.target_entity_id}
                  onChange={(e) => updatePolicyField('target_entity_id', e.target.value)}
                >
                  <option value="">Select target...</option>
                  {entities.map((ent) => (
                    <option key={ent.id} value={ent.id}>
                      {ent.name}
                    </option>
                  ))}
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tier Ceiling
                </label>
                <Select
                  value={policyForm.tier_ceiling}
                  onChange={(e) => updatePolicyField('tier_ceiling', Number(e.target.value))}
                >
                  {[1, 2, 3, 4, 5].map((t) => (
                    <option key={t} value={t}>
                      Tier {t}
                    </option>
                  ))}
                </Select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Status
                </label>
                <Select
                  value={policyForm.status}
                  onChange={(e) => updatePolicyField('status', e.target.value)}
                >
                  <option value="active">Active</option>
                  <option value="draft">Draft</option>
                  <option value="disabled">Disabled</option>
                </Select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Permitted Categories (comma-separated)
              </label>
              <TextInput
                value={policyForm.permitted_categories}
                onChange={(e) => updatePolicyField('permitted_categories', e.target.value)}
                placeholder="e.g. data_sync, reporting, compliance"
              />
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <div className="flex justify-end gap-3 w-full">
            <Button color="gray" onClick={closePolicyModal} disabled={policySubmitting}>
              Cancel
            </Button>
            <Button color="blue" onClick={handlePolicySubmit} disabled={policySubmitting}>
              {policySubmitting ? (
                <>
                  <Spinner size="sm" className="mr-2" />
                  {editingPolicy ? 'Updating...' : 'Creating...'}
                </>
              ) : editingPolicy ? (
                'Update Policy'
              ) : (
                'Create Policy'
              )}
            </Button>
          </div>
        </Modal.Footer>
      </Modal>

      {/* Delete Policy Confirmation Modal */}
      <Modal show={deleteModalOpen} onClose={closeDeletePolicyModal} size="md" popup>
        <Modal.Header />
        <Modal.Body>
          <div className="text-center">
            <HiExclamation className="mx-auto mb-4 h-14 w-14 text-red-400" />
            <h3 className="mb-2 text-lg font-semibold text-gray-900">
              Delete Policy
            </h3>
            <p className="mb-5 text-sm text-gray-500">
              Are you sure you want to delete{' '}
              <span className="font-medium">{deletingPolicy?.policy_name}</span>? This action
              cannot be undone.
            </p>
            <div className="flex justify-center gap-4">
              <Button color="failure" onClick={handleDeletePolicy} disabled={policyDeleting}>
                {policyDeleting ? 'Deleting...' : 'Yes, delete'}
              </Button>
              <Button color="gray" onClick={closeDeletePolicyModal} disabled={policyDeleting}>
                Cancel
              </Button>
            </div>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
};
